import { Transition, FieldCodePostalVille, FieldInput, FieldRadioGroup, FieldPatternInput } from '@workspace/core-ts/components';
import { useGlobalForm } from '@workspace/core-ts/contexts';
import { isNotInTheFuture, isNotMoreThan31DaysInTheFuture, isOlderThan30Years, isValidImmatriculation } from '@workspace/utils';
import { booleanOptions, titulaireCarteGriseOptions } from '@workspace/utils';
import { DocumentDuplicateIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import type { FormSchemaType } from '@/schema';
import ModalVehiculier from '@/components/modals/modal-vehiculier';
import { ButtonVehiculier } from './smart-form/button-vehiculier';

const RESTRICTED_PREFIXES = {
  all: ['97', '98', '20', '13001', '13002', '13003', '13004', '13005', '13006',
        '13007', '13008', '13009', '13010', '13011', '13012', '13013', '13014',
        '13015', '13016'],
  direct: ['97', '98', '20', '13', '06', '83'],
};

const defaultVehicule = {
    IdentifiantSRA: null,
    DateMEC: '',
    Immatriculation: '',
    DateAchatVehicule: '',
    CarteGriseFrancaise: null,
    TitulaireCarteGrise: null,
    Marque: null,
    Modele: null,
    CodePostalGarage: null,
    VilleGarage: null,
    ListeOptionsChoisies: [],
};

const maxVehicules = 50;

export default function VehiculesFields() {
    const methods = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control: methods.control,
        name: 'DemandeTarif.ListeVehicules',
        shouldUnregister: false,
    });

    const vehicules = methods.watch('DemandeTarif.ListeVehicules', []);
    const isAvenant = methods.watch('DemandeTarif.ActeGestion') === 'AV';

    useEffect(() => {
        if (vehicules?.length === 0 && !isAvenant) append(defaultVehicule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicules]);

    const [visibleItems, setVisibleItems] = useState(fields.map((field) => field.id)); // Initialise avec tous les IDs visibles

    useEffect(() => {
        setVisibleItems(fields.map((field) => field.id));
    }, [fields]);

    const handleRemove = (index: number) => {
        if (fields.length === 1 || visibleItems.length === 1) return;
        const idToRemove = fields[index].id;
        setVisibleItems((currentVisibleItems) => currentVisibleItems.filter((id) => id !== idToRemove));
        methods.clearErrors();

        setTimeout(() => {
            remove(index);
        }, 300); // Assurez-vous que ce délai correspond à la durée de votre animation de sortie
    };

    const duplicateVehicule = (index: number) => {
        if (fields.length >= maxVehicules) return;
        const vehicule = methods.getValues(`DemandeTarif.ListeVehicules.${index}`);
        methods.clearErrors();
        append({
            ...vehicule,
            Immatriculation: '',
        });
    };

    const addVehicule = () => {
        if (fields.length >= maxVehicules) return;

        methods.clearErrors();
        append(defaultVehicule);
    };

    return (
        <>
            <ModalVehiculier />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 w-full my-4">
                {fields.map((field, index) => (
                    <VehiculierItem duplicateVehicule={duplicateVehicule} field={field} handleRemove={handleRemove} index={index} key={field.id} requiredImmat={false} visibleItems={visibleItems} />
                ))}

                <div className="h-full w-full flex flex-col">
                    {fields.length >= maxVehicules ? (
                        <div className="min-h-20 flex-1 py-10 border border-secondary-light text-center bg-secondary-light rounded-lg flex flex-col items-center justify-center gap-2 col-span-1">
                            <p className="text-secondary text-sm">Vous avez atteint le nombre maximum de véhicules</p>
                        </div>
                    ) : (
                        <button
                            className="group border border-primary min-h-20 flex-1 py-10 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 col-span-1 hover:bg-card transition-all duration-200 ease-in-out"
                            onClick={addVehicule}
                            type="button"
                        >
                            <span className="rounded-md bg-card p-2">
                                <PlusIcon className="w-6 h-6 stroke-primary" />
                            </span>

                            <span className="text-primary text-sm group-hover:underline">Ajouter un véhicule</span>
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}

function VehiculierItem({ field, visibleItems, index, handleRemove, duplicateVehicule }: any) {
    const methods = useFormContext<FormSchemaType>();
    const { others } = useGlobalForm();

    const marque = methods.watch(`DemandeTarif.ListeVehicules.${index}.Marque`),
        modele = methods.watch(`DemandeTarif.ListeVehicules.${index}.Modele`);

    useEffect(() => {
        methods.clearErrors(`DemandeTarif.ListeVehicules.${index}.Marque`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marque, modele]);

    const validateCodePostal = useCallback((value: string) => {
        if (!/^\d{5}$/.test(value)) {
            return 'Le code postal doit être composé de 5 chiffres';
        }

        const prefixes = others?.apporteur1?.codeBelair === 43397
            ? RESTRICTED_PREFIXES.direct
            : RESTRICTED_PREFIXES.all;

        if (prefixes.some(prefix => value.startsWith(prefix))) {
            return 'Nous ne sommes pas en mesure d\'assurer les véhicules sur cette ville.';
        }

        return undefined;
    }, [others?.apporteur1?.codeBelair]);

    return (
        <Transition
            appear
            as="div"
            className="flex flex-col space-y-4 border p-4 rounded-md bg-card border-card"
            enter="transition-opacity duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            key={field.id}
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={visibleItems.includes(field.id)}
        >
            <div className="flex justify-between items-center" id={`vehicule-${index}`}>
                <h3 className="text-base text-primary font-medium pt-1">Véhicule {index + 1}</h3>

                <div className="flex gap-1">
                    <button className="px-2 text-xs py-1 rounded-md text-black bg-[#efbe02] hover:bg-[#efbe02]/50 flex items-center justify-center gap-1" onClick={() => duplicateVehicule(index)} type="button">
                        <DocumentDuplicateIcon className="w-5 h-5" strokeWidth={1} />
                        Dupliquer
                    </button>
                    {methods.watch('DemandeTarif.ListeVehicules', []).length > 1 && (
                        <button className="p-1 rounded-md hover:bg-white flex items-center justify-center" onClick={() => handleRemove(index)} type="button">
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>

            <ButtonVehiculier index={index} />

            <FieldCodePostalVille
                label="Code postal & ville de stationnement"
                nameCP={`DemandeTarif.ListeVehicules.${index}.CodePostalGarage`}
                nameVille={`DemandeTarif.ListeVehicules.${index}.VilleGarage`}
                customValidation={validateCodePostal}
            />
            <FieldPatternInput
                label="Date de mise en circulation"
                name={`DemandeTarif.ListeVehicules.${index}.DateMEC`}
                type="date"
                validation={{
                    validate: {
                        isNotInTheFuture,
                        isOlderThan30Years,
                    },
                }}
            />

            <FieldInput
                label="Immatriculation"
                name={`DemandeTarif.ListeVehicules.${index}.Immatriculation`}
                placeholder="AA 123 BB"
                required={false}
                validation={{
                    validate: {
                        isValidImmatriculation,
                        notAlreadyExists: (value: string) => {
                            if (methods.watch('DemandeTarif.ListeVehicules').filter((v: any) => v.Immatriculation && v.Immatriculation === value).length > 1) {
                                return 'Cette immatriculation est déjà utilisée';
                            }
                            return true;
                        },
                    },
                    onChange: (e) => {
                        const value = e.target.value;

                        // ONLY DIGITS AND LETTERS
                        e.target.value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                    },
                }}
            />
            <FieldRadioGroup
                label="Le véhicule fait-il l'objet d'un financement en leasing (LOA ou LLD) ?"
                name={`DemandeTarif.ListeVehicules.${index}.Leasing`}
                options={[
                    { label: 'Oui', value: true },
                    { label: 'Non', value: false },
                ]}
                type="small"
            />

            <FieldPatternInput
                label="Date d'acquisition"
                name={`DemandeTarif.ListeVehicules.${index}.DateAchatVehicule`}
                type="date"
                validation={{
                    validate: {
                        isNotMoreThan31DaysInTheFuture,
                        isOlderThan30Years,
                        isDateAchatAfterDateMEC: (value: string) => {
                            const dateMEC = methods.watch(`DemandeTarif.ListeVehicules.${index}.DateMEC`);
                            if (dateMEC && value) {
                                return new Date(value) < new Date(dateMEC) ? 'La date d’achat ne peut pas être antérieure à la date de mise en circulation' : true;
                            }
                            return true;
                        },
                    },
                }}
            />

            <FieldRadioGroup
                className="text-sm"
                label="La carte grise est-elle française ?"
                name={`DemandeTarif.ListeVehicules.${index}.CarteGriseFrancaise`}
                options={booleanOptions}
                type="small"
                validation={{
                    validate: (value) => (value === false ? 'La carte grise du véhicule doit être française. Dans le cas contraire, le véhicule ne pourra pas être assuré.' : true),
                }}
            />
            <FieldRadioGroup
                className="text-sm"
                col={1}
                label="Qui est le titulaire de la carte grise ?"
                name={`DemandeTarif.ListeVehicules.${index}.TitulaireCarteGrise`}
                options={titulaireCarteGriseOptions}
                type="small"
                validation={{
                    validate: (value) => (value === titulaireCarteGriseOptions[2].value ? 'Le titulaire de la carte grise doit être la société ou vous-même.' : true),
                }}
            />
        </Transition>
    );
}
